/**
 * CoopersWalletDashboard.jsx
 * Dashboard COOPER(s) · Conseiller ESS avec système de crédits
 * 
 * Les crédits sont débités automatiquement lors des actions productives
 * La consultation reste gratuite
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent } from './ui/dialog';
import { 
  Wallet, Loader2, BarChart3, History, Coins, RefreshCw,
  TrendingUp, HelpCircle, BookOpen, Calculator, Info
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import CreditsPricingGuide from './CreditsPricingGuide';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Actions COOPER(s) pour l'historique
const COOPERS_ACTIONS = {
  coopers_sim_standard: { label: "Simulation standard", credits: 2 },
  coopers_sim_advanced: { label: "Simulation avancée", credits: 3 },
  coopers_doc_standard: { label: "Document standard", credits: 2 },
  coopers_doc_custom: { label: "Document personnalisé ESS", credits: 4 },
  coopers_share_secure: { label: "Partage sécurisé", credits: 1 },
  coopers_msg_short: { label: "Message court", credits: 1 },
};

const CoopersWalletDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPricingGuide, setShowPricingGuide] = useState(false);

  const fetchWalletData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [walletRes, statsRes] = await Promise.all([
        fetch(`${API}/api/wallets/me`, { headers }).then(r => r.json()),
        fetch(`${API}/api/wallets/coopers/stats`, { headers }).then(r => r.json()).catch(() => ({ success: false }))
      ]);
      
      setWallet(walletRes);
      if (statsRes.success) {
        setStats(statsRes);
      }
    } catch {
      toast.error('Erreur lors du chargement du wallet');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec solde wallet */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Calculator className="h-7 w-7 text-blue-600" />
            COOPER(s) · Conseiller ESS
          </h2>
          <p className="text-slate-600">Gestion des UC et actions productives</p>
        </div>
        <div className="flex items-center gap-4">
          <Button onClick={fetchWalletData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Card className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
            <CardContent className="py-3 px-5">
              <div className="flex items-center gap-3">
                <Wallet className="h-6 w-6" />
                <div>
                  <p className="text-xs opacity-80">Solde wallet</p>
                  <p className="text-xl font-bold">{wallet?.credit_balance?.toFixed(0) || 0} UC</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mission COOPER(s) */}
      <Card className="border-l-4 border-l-blue-500 bg-blue-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-800">Mission COOPER(s) - Conseiller ESS</p>
              <ul className="text-sm text-blue-700 mt-1 space-y-1">
                <li>✅ Consultation / navigation : <strong>0 UC</strong></li>
                <li>✅ Brouillon / prévisualisation : <strong>0 UC</strong></li>
                <li>💰 Action productive (simulation validée, document final, partage) : <strong>débit automatique</strong></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques COOPER(s) */}
      {stats?.coopers_stats && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Statistiques COOPER(s)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600">Actions ce mois</p>
                <p className="text-2xl font-bold text-slate-800">{stats.coopers_stats.total_actions || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-sm text-blue-600">Crédits utilisés</p>
                <p className="text-2xl font-bold text-blue-700">{stats.coopers_stats.total_credits_consumed || 0}</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-sm text-green-600">Solde disponible</p>
                <p className="text-2xl font-bold text-green-700">{wallet?.credit_balance?.toFixed(0) || 0}</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <p className="text-sm text-amber-600">Ce mois</p>
                <p className="text-2xl font-bold text-amber-700">{wallet?.credits_used_this_month?.toFixed(0) || 0}</p>
              </div>
            </div>
            
            {/* Stats par catégorie */}
            {stats.coopers_stats.by_category && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(stats.coopers_stats.by_category).map(([category, credits]) => (
                  <div key={category} className="bg-slate-100 rounded-lg p-3">
                    <p className="text-xs text-slate-500 capitalize">{category}</p>
                    <p className="text-lg font-semibold text-slate-700">{credits} UC</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Historique récent */}
      {stats?.recent_transactions && stats.recent_transactions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-slate-600" />
              Historique récent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_transactions.slice(0, 5).map((tx, index) => {
                const actionConfig = COOPERS_ACTIONS[tx.action_type] || {};
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        tx.credits_delta < 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                      }`}>
                        {tx.credits_delta < 0 ? '-' : '+'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{actionConfig.label || tx.description}</p>
                        <p className="text-xs text-slate-500">
                          {tx.metadata?.dossier && <span className="mr-2">📁 {tx.metadata.dossier}</span>}
                          {new Date(tx.created_at).toLocaleDateString('fr-FR', { 
                            day: '2-digit', 
                            month: 'short', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </p>
                      </div>
                    </div>
                    <Badge variant={tx.credits_delta < 0 ? 'destructive' : 'success'}>
                      {tx.credits_delta < 0 ? '' : '+'}{Math.abs(tx.credits_delta)} UC
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comment fonctionnent les crédits - Section explicative */}
      <Card className="border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-emerald-800">
            <HelpCircle className="h-5 w-5 text-emerald-600" />
            Comment fonctionnent les UC ?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Système de consommation */}
          <div className="bg-white rounded-lg p-4 border border-emerald-100">
            <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <Coins className="h-4 w-4 text-amber-500" />
              Système de consommation automatique
            </h4>
            <p className="text-sm text-slate-600 leading-relaxed">
              Chaque action que vous effectuez sur la plateforme est comptabilisée. 
              <strong className="text-emerald-700"> Tous les 100 clics</strong>, 
              <strong className="text-amber-600"> 13 UC</strong> sont automatiquement débités de votre solde.
              Ce système permet une facturation équitable basée sur votre utilisation réelle de la plateforme.
            </p>
          </div>

          {/* Exemptions */}
          <div className="bg-white rounded-lg p-4 border border-emerald-100">
            <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-500" />
              Ce qui est gratuit
            </h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                La consultation et la navigation dans l'espace
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Les brouillons et prévisualisations de documents
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                L'accès aux ressources et à la documentation
              </li>
            </ul>
          </div>

          {/* Actions payantes */}
          <div className="bg-white rounded-lg p-4 border border-emerald-100">
            <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-500" />
              Actions consommant des UC
            </h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-amber-500">💰</span>
                Simulations validées et finalisées
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-500">💰</span>
                Génération de documents définitifs (PDF)
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-500">💰</span>
                Partages et envois sécurisés
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-500">💰</span>
                Messagerie et communications archivées
              </li>
            </ul>
          </div>

          {/* Bouton pour plus de détails */}
          <Button 
            variant="outline" 
            onClick={() => setShowPricingGuide(true)}
            className="w-full flex items-center justify-center gap-2 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
          >
            <BookOpen className="h-4 w-4" />
            Voir le barème détaillé des UC
          </Button>
        </CardContent>
      </Card>

      {/* Clause contractuelle ESS */}
      <Card className="bg-slate-50">
        <CardContent className="py-4">
          <p className="text-xs text-slate-500 italic">
            « Le COOPER(s) – Conseiller ESS intervient dans le cadre d&apos;une prestation d&apos;ingénierie, 
            de simulation et de production documentaire. L&apos;usage des outils est matérialisé par des UC, 
            indépendants de toute logique d&apos;assurance ou de rémunération horaire. »
          </p>
        </CardContent>
      </Card>

      {/* Modal du guide des crédits détaillé */}
      <Dialog open={showPricingGuide} onOpenChange={setShowPricingGuide}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <CreditsPricingGuide userRole="cooper" onClose={() => setShowPricingGuide(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoopersWalletDashboard;
